import {
  DocSourceType,
  ISODateString,
  Timestamps,
  UUID,
} from "./common";

export interface Document extends Timestamps {
  id: UUID;
  program_id: UUID;
  stage_id?: UUID | null;
  grade_id?: UUID | null;
  subject_id?: UUID | null;
  title: string;
  description?: string | null;
  language?: string | null;
  tags: string[];
  source_type: DocSourceType;
  source_url?: string | null;
  is_active: boolean;
  is_exam: boolean;
  is_exercice: boolean;
  is_course: boolean;
  created_by?: UUID | null;
  unique_key?: string | null;

  /* Identité du chapitre : écrite par l'import ou par PATCH /chapter, jamais
     par le PUT du document. */
  chapter_ref?: string | null;
  chapter_order?: number | null;
  semester?: number | null;
  /** Filière ; null = document commun à toutes les filières du niveau. */
  track_id?: UUID | null;

  /** Relecture humaine (liste des documents) ; absent sur un serveur ancien. */
  review_status?: ReviewStatus;
  /**
   * Interrupteur de la matière (liste des documents). Le cours n'est servi aux
   * élèves que si `is_active` ET `subject_active`.
   */
  subject_active?: boolean;
}

/**
 * État de relecture humaine d'un chapitre. Informatif : un chapitre à relire
 * reste jouable.
 */
export type ReviewStatus = "non_requise" | "a_relire" | "validee" | "a_corriger";

/** Un changement d'état de relecture (import ou décision d'un relecteur). */
export interface ReviewEvent {
  id: number;
  status: ReviewStatus;
  doc_version_id: UUID | null;
  version: number | null;
  comment: string | null;
  /** `editor:<id>`, `editor:internal` ou `import`. */
  actor: string;
  actor_name: string | null;
  created_at: ISODateString;
}

/** `GET /v1/documents/:id/review`. */
export interface DocumentReview {
  document_id: UUID;
  status: ReviewStatus;
  /** Version sur laquelle porte l'état. */
  doc_version_id: UUID | null;
  version: number | null;
  latest_version_id: UUID | null;
  latest_version: number | null;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_at: ISODateString | null;
  comment: string | null;
  /** Bloc `relecture` du manifeste de la dernière version. */
  required: boolean;
  group: string | null;
  source_status: string | null;
  /** Ce que la relecture automatique a corrigé : à vérifier en priorité. */
  points: string[];
  /** Plus récent d'abord. */
  history: ReviewEvent[];
}

/** `POST /v1/documents/:id/review`. */
export interface ReviewDecisionPayload {
  statut: "validee" | "a_corriger";
  /** Obligatoire pour « a_corriger ». */
  commentaire?: string;
  /** Version relue : 409 si une autre a été importée entre-temps. */
  version?: number;
}

/** Les quatre champs que seul `PATCH /v1/documents/:id/chapter` écrit. */
export const CHAPTER_FIELDS = [
  "chapter_ref",
  "chapter_order",
  "semester",
  "track_id",
] as const;

export type ChapterField = (typeof CHAPTER_FIELDS)[number];

export type DocumentChapterPatch = Partial<Pick<Document, ChapterField>>;

export interface DocumentVersion {
  id: UUID;
  document_id: UUID;
  version: number;
  storage_key?: string | null;
  storage_bucket?: string | null;
  mime_type?: string | null;
  byte_size?: number | null;
  page_count?: number | null;
  sha256_hex?: string | null;
  notes?: string | null;
  status: string;
  created_by?: UUID | null;
  created_at: ISODateString;
}
